import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { Select } from '@ngxs/store';
import { Instance } from '@nutrient-sdk/viewer';
import { CertificateRecord } from 'app/library/certificate-record/models/certificate-record';
import { CertificateRecordState } from 'app/library/certificate-record/models/certificate-record.state';
import { CertificateRecordApiService } from 'app/library/certificate-record/services/certificate-record.api-service';
import { CertificateRecordService } from 'app/library/certificate-record/services/certificate-record.service';
import { ReplaceCertificateDialogComponent } from 'app/library/certification-provider/components/dialogs/replace-certificate-dialog/replace-certificate-dialog.component';
import { CertificationFlatNode } from 'app/library/certification-provider/components/providers-all-list/providers-all-list.component';
import { CertificationProvider } from 'app/library/certification-provider/models/certification-provider';
import { CertificationProviderService } from 'app/library/certification-provider/services/certification-provider.service';
import { Certification } from 'app/library/certifications/models/certification';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { FuseConfigService } from 'app/projects/fuse/src/lib/services/config.service';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-edit-certificate-mobile-page',
    templateUrl: './edit-certificate-mobile-page.component.html',
    styleUrls: ['./edit-certificate-mobile-page.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class EditCertificateMobilePageComponent extends UnsubscribeOnDestroy implements OnInit {
    @Select(CertificateRecordState.getEntity)
    certificateRecord$: Observable<CertificateRecord>;
    certificateRecord: CertificateRecord;

    selectedCertificationProviderId: string;
    selectedCertificationId: string;
    selectedCertification: Certification;

    isToDoFeatureEnabled: boolean = false;
    certificationForm: FormGroup;
    fileURL: string = '';
    doesCertificateFileExist: boolean = false;
    uploadedCertificateFiles: File[] = [];
    selectedCertificateToReplace: CertificationFlatNode;
    selectedCertificateToBeReplacedId: string;
    isFileUpdated: boolean = false;
    minDate: Date;
    isLoading: boolean = true;
    hasEndDate = new FormControl(false);

    isSaveButtonLoading: boolean = false;
    isEndDateToggleChanged: boolean = false;
    isDragOver = false;

    private _instance: Instance;

    private _setToolbarItems(instance = this._instance): void {
        if (!instance) {
            return;
        }

        instance.setToolbarItems((items) => {
            items = items.filter((item) => item.type === 'zoom-in' || item.type === 'zoom-out');

            return items;
        });
    }

    constructor(
        private _certificationProviderService: CertificationProviderService,
        private _certificateRecordService: CertificateRecordService,
        private _certificateRecordApiService: CertificateRecordApiService,
        private _featureService: FeatureService,
        private _fb: FormBuilder,
        private _matDialog: MatDialog,
        private _fuseConfigService: FuseConfigService
    ) {
        super();

        this.isToDoFeatureEnabled = this._featureService.checkFeatureStatus(FeatureSwitchName.ToDo);

        this._fuseConfigService.config = {
            layout: {
                toolbar: {
                    hidden: true,
                },
                navbar: {
                    hidden: true,
                },
            },
        };
    }

    ngOnInit(): void {
        this.certificateRecord$.pipe(takeUntil(this._unsubscribeAll)).subscribe((certificateRecord) => {
            if (certificateRecord) {
                this.isLoading = true;
                this.certificateRecord = certificateRecord;
                this.selectedCertification = this.certificateRecord.officialCertificateMetadata.certification;
                this.selectedCertificationId = this.selectedCertification.id;
                this.selectedCertificationProviderId = this.selectedCertification.provider.id;

                this.certificationForm = this._fb.group({
                    provider: [this.certificateRecord.officialCertificateMetadata.certification.provider.id, Validators.required],
                    certification: [this.certificateRecord.officialCertificateMetadata.certification.id, Validators.required],
                    issueDate: [this.certificateRecord.officialCertificateMetadata.issueDate, Validators.required],
                });

                if (
                    this.certificateRecord.officialCertificateMetadata.certification.hasExpirationDate ||
                    (this.certificateRecord.officialCertificateMetadata.certification.certificationName === 'other' &&
                        this.certificateRecord.officialCertificateMetadata.expirationDate)
                ) {
                    this.certificationForm.addControl('expirationDate', new FormControl(this.certificateRecord.officialCertificateMetadata.expirationDate, Validators.required));

                    if (
                        this.certificateRecord.officialCertificateMetadata.certification.certificationName === 'other' &&
                        this.certificateRecord.officialCertificateMetadata.expirationDate
                    ) {
                        this.hasEndDate.setValue(true);
                    }
                }

                if (
                    this.isToDoFeatureEnabled &&
                    (this.certificateRecord.officialCertificateMetadata.certification.hasExpirationDate ||
                        (this.certificateRecord.officialCertificateMetadata.certification.certificationName === 'other' &&
                            this.certificateRecord.officialCertificateMetadata.expirationDate))
                ) {
                    this.certificationForm.addControl('notifyAboutExpiration', new FormControl(this.certificateRecord.officialCertificateMetadata.notifyAboutExpiration));
                }

                this.updateExpirationDate();
                this.loadCertificateFile();
                this.isLoading = false;
            }
        });
    }

    private loadCertificateFile(): void {
        this._certificateRecordApiService
            .getCertificateFile(this.certificateRecord.id)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(
                (value) => {
                    this.fileURL = URL.createObjectURL(value);
                },
                (error) => {
                    console.error('Error loading certificate file', error);
                }
            );
    }

    updateExpirationDate(): void {
        const startDate = this.certificationForm.get('issueDate').value;

        this.minDate = startDate ? new Date(startDate) : new Date();
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;
    }

    onFileDropped(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;

        if (event.dataTransfer && event.dataTransfer.files.length > 0) {
            const files: File[] = Array.from(event.dataTransfer.files);
            this.handleFiles(files);
        }
    }

    handleFiles(files: File[]): void {
        const fileSubject = new BehaviorSubject<File | null>(null);
        const collectedFiles: File[] = [];

        fileSubject
            .asObservable()
            .pipe(
                filter((file) => !!file),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((file) => {
                collectedFiles.push(file!);
                if (collectedFiles.length === files.length) {
                    fileSubject.complete();
                    this.fileChange(collectedFiles);
                }
            });

        files.forEach((file) => fileSubject.next(file));
    }

    fileChange(files: FileList | File[]): void {
        this.isFileUpdated = true;
        if (files instanceof FileList) {
            const temp: File[] = [];
            for (const key of Object.keys(files)) {
                temp[key] = files[key];
            }
            files = temp;
        }

        this.uploadedCertificateFiles = files;
    }

    downloadCertificate(): void {
        this._certificateRecordService.downloadCertificate(this.certificateRecord.id);
    }

    onRemoveAddedCertificate(): void {
        this.uploadedCertificateFiles = [];
    }

    onRemoveReplacementCertificate(): void {
        this.selectedCertificateToReplace = null;
    }

    load(instance: Instance): void {
        this._setToolbarItems(instance);

        this._instance = instance;
    }

    async openReplaceCertificateDialog(certificate: CertificationFlatNode): Promise<void> {
        const dialogRef = this._matDialog.open(ReplaceCertificateDialogComponent, {
            width: '976px',
        });

        dialogRef.componentInstance.certificateToBeReplaced = certificate;
        dialogRef.componentInstance.buttonText = 'GENERAL.BUTTON.CONFIRM';

        dialogRef.afterClosed().subscribe(async (result: { newSelectedCertificate: CertificationFlatNode }) => {
            if (result) {
                this.selectedCertificateToReplace = result.newSelectedCertificate;
                this.selectedCertificateToBeReplacedId = certificate.id;

                this.certificationForm.markAsDirty();
            }
        });
    }

    onProviderSelected(provider: CertificationProvider): void {
        this.selectedCertificationProviderId = provider.id;
        this.selectedCertificationId = null;
        this.selectedCertification = null;
        this.hasEndDate.setValue(false);
        this.certificationForm.removeControl('expirationDate');
        if (this.isToDoFeatureEnabled) {
            this.certificationForm.removeControl('notifyAboutExpiration');
        }
    }

    onCertificationSelected(certification: Certification): void {
        this.selectedCertificationId = certification.id;
        this.selectedCertification = certification;

        if (this.selectedCertification.certificationName === 'other') {
            this.hasEndDate.setValue(true);
        } else {
            this.hasEndDate.setValue(false);
        }

        if (this.selectedCertification.hasExpirationDate || this.selectedCertification.certificationName === 'other') {
            if (!this.certificationForm.get('expirationDate')) {
                this.certificationForm.addControl(
                    'expirationDate',
                    new FormControl(this.certificateRecord.officialCertificateMetadata.expirationDate, Validators.required)
                );
            }
            if (this.isToDoFeatureEnabled && !this.certificationForm.get('notifyAboutExpiration')) {
                this.certificationForm.addControl('notifyAboutExpiration', new FormControl(true));
            }
        } else {
            this.certificationForm.removeControl('expirationDate');
            if (this.isToDoFeatureEnabled) {
                this.certificationForm.removeControl('notifyAboutExpiration');
            }
        }
    }

    onChangeEndDateToggle(event: MatCheckboxChange): void {
        if (event.checked) {
            this.isEndDateToggleChanged = false;
            this.certificationForm.addControl('expirationDate', new FormControl(null, Validators.required));
            if (this.isToDoFeatureEnabled) {
                this.certificationForm.addControl('notifyAboutExpiration', new FormControl(false));
            }
        } else {
            this.isEndDateToggleChanged = true;
            this.certificationForm.removeControl('expirationDate');
            if (this.isToDoFeatureEnabled) {
                this.certificationForm.removeControl('notifyAboutExpiration');
            }
        }
    }

    async saveSelected(): Promise<void> {
        this.isSaveButtonLoading = true;

        this.isSaveButtonLoading = false;

        await this._certificateRecordService.updateCertificateRecord(
            this.certificateRecord.id,
            this.certificationForm.get('certification').value,
            this.certificationForm.get('issueDate').value,
            this.certificationForm.get('expirationDate')?.value,
            this.certificationForm.get('notifyAboutExpiration')?.value
        );

        if (this.selectedCertificateToReplace) {
            await this._certificationProviderService.replaceCertificate(this.selectedCertificateToBeReplacedId, this.selectedCertificateToReplace.id);
        }

        if (this.isFileUpdated) {
            await this._certificateRecordApiService.uploadCertificateFile(this.certificateRecord.id, this.uploadedCertificateFiles[0]);
        }
    }
}
